<template>
    <div class="w-full h-full">
        <div class="flex justify-center">
            <h1 class="text-3xl">
                Dividir y asignar proveedores a orden
                <a
                    :href="`/app/orders/detail/${itemComponent.id}`"
                    class="text-theme-primary"
                >
                    <strong> #{{ itemComponent.id }} </strong>
                </a>
            </h1>
        </div>
        <div id="info-container" class="flex flex-wrap">
            <div class="w-7/12"></div>
            <div
                class="lg:w-5/12 w-full mt-2 flex items-center justify-between"
            >
                <small for="swDateBetween">
                    ¿ Necesitas ayuda para asignar proveedores ?
                </small>
                <app-button
                    icon
                    border
                    :active="enableHelp"
                    @click="enableHelp = !enableHelp"
                >
                    ℹ️
                </app-button>
            </div>
        </div>
        <process-guide
            v-model="enableHelp"
            title="¿Cómo dividir y asignar proveedores?"
            :steps="[
                'Verifica si el pedido es a domicilio o en tienda. Si hay inventario en la tienda donde se recogerá el pedido, asigna los productos a esa tienda.',
                'Revisa qué productos tienen inventario habilitado en alguna tienda: los que tienen inventario disponible se marcan con borde azul, y los que no, con borde rojo.',
                'Selecciona el producto: el sistema habilitará solo las tiendas que tengan inventario de ese producto, y mostrará las unidades disponibles y el precio de venta al público.',
                'Cuando todos los productos estén asignados, haz clic en el botón Dividir y asignar proveedores al final de la página.',
                'Confirma si quieres decrementar el inventario de cada tienda al fragmentar la orden, o solo fragmentarla. El sistema creará una orden madre y una orden hija por cada tienda.'
            ]"
        />
        <div v-if="hasLoadedBase" class="my-4">
            <div v-if="itemComponent.supplyMerchant">
                <div class="bg-yellow-200 text-black px-3 rounded-md">
                    <span>
                        Orden asignada a
                        {{
                            itemComponent.supplyMerchant.merchantType
                                | merchantInfo("type")
                        }}
                        {{
                            `${itemComponent.supplyMerchant.name} Dirección ${itemComponent.supplyMerchant.address} / ${itemComponent.supplyMerchant.addressComplement}`
                        }}
                    </span>
                </div>
            </div>
            <div class="mt-2">
                <div v-if="itemComponent.location">
                    <span class="h5">
                        Domicilio para entregar a
                        <strong>
                            {{
                                itemComponent.location.address
                                    | address
                                    | cut(28)
                            }}
                        </strong>
                        , barrio {{ itemComponent.location.neighborhood }} de
                        {{ itemComponent.location.city }}
                    </span>
                </div>
                <div v-if="itemComponent.pickupMerchant">
                    <span class="h5">
                        Entregar en la
                        {{
                            itemComponent.pickupMerchant.merchantType
                                | merchantInfo("type")
                        }}
                        <strong class="font-italic">
                            {{ itemComponent.pickupMerchant.name }}
                        </strong>
                        ubicada
                        {{
                            itemComponent.pickupMerchant.address
                                | address
                                | cut(28)
                        }}, barrio
                        {{ itemComponent.pickupMerchant.neighborhood }} de
                        {{ itemComponent.pickupMerchant.city }}
                    </span>
                </div>
            </div>

            <div class="flex overflow-x-auto mt-4">
                <div class="mr-1">
                    <div
                        class="border-4 rounded-lg border-secondary w-80 h-full px-1 pt-3"
                    >
                        <div>
                            <h3 class="text-lg">🛍 Productos</h3>
                            <span>
                                Mueve los productos de la orden a cada tienda.
                            </span>
                        </div>

                        <Container
                            class="h-3/4"
                            group-name="order"
                            :get-child-payload="getReferencesPayload()"
                            drag-class="border-warning border-2"
                            drop-class="border-secondary border-4"
                            :drop-placeholder="$data.$dropPlaceholderOptions"
                            @drag-start="onStartDrag"
                            @drag-end="onEndDrag"
                            @drop="onDropReferenceOfReferences"
                        >
                            <Draggable
                                v-for="item in references"
                                :key="item.id"
                            >
                                <reference-table-item-2
                                    :class="
                                        !item.merchants.length &&
                                        'border-danger border-4'
                                    "
                                    class="my-2"
                                    :reference="item"
                                />
                            </Draggable>
                        </Container>
                    </div>
                </div>

                <div
                    v-for="column in available"
                    :key="`${column.merchantType}:${column.merchantId}`"
                    class="mr-1"
                >
                    <div
                        class="border-4 rounded-lg border-secondary w-80 px-1 pt-2 h-full"
                    >
                        <div>
                            <h3 class="text-theme-primary capitalize text-xl">
                                {{
                                    column.merchantType
                                        | merchantInfo("icon", "type")
                                }}
                                <span
                                    :class="{
                                        'font-italic':
                                            itemComponent.pickupMerchant &&
                                            column.merchantType ===
                                                itemComponent.pickupMerchant
                                                    .merchantType &&
                                            itemComponent.pickupMerchant
                                                .merchantId ===
                                                column.merchantId
                                    }"
                                >
                                    {{ column.name }}
                                </span>
                            </h3>
                            <span>
                                {{ column.address | address | cut(28) }}
                                {{
                                    `${column.addressComplement}, barrio ${column.neighborhood} de ${column.city}`
                                }}
                            </span>

                            <div
                                v-if="column.stockMerchant"
                                class="my-2 flex flex-col text-sm"
                            >
                                <div class="flex">
                                    <span> Stock tienda: </span>
                                    <span class="ml-1">
                                        {{ column.stockMerchant }}
                                    </span>
                                </div>
                                <div class="flex">
                                    <span> Precio tienda: </span>
                                    <span class="ml-1">
                                        {{ column.priceOfferMerchant | money }}
                                    </span>
                                </div>
                                <div class="flex">
                                    <span> Precio acordado: </span>
                                    <span class="ml-1">
                                        {{ column.priceAgreedMerchant | money }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Container
                            group-name="order"
                            class="h-3/4"
                            :get-child-payload="
                                getReferencesInMerchantPayload(column)
                            "
                            drag-class="border-warning"
                            drop-class="border-secondary"
                            :drop-placeholder="$data.$dropPlaceholderOptions"
                            @drag-start="onStartDrag"
                            @drop="(e) => onDropReferenceOfMerchant(column, e)"
                            @drag-end="onEndDrag"
                        >
                            <Draggable
                                v-for="item in column.children"
                                :key="item.id"
                            >
                                <reference-table-item-2
                                    class="my-2"
                                    :reference="item"
                                />
                            </Draggable>
                        </Container>
                    </div>
                </div>
                <div v-if="!available.length">
                    <div
                        class="border-4 rounded-lg border-danger w-80 px-4 pt-3 h-full flex items-center"
                    >
                        <strong class="text-danger">
                            🚨 Producto sin inventario habilitado
                        </strong>
                    </div>
                </div>
            </div>

            <app-button
                class="my-8 w-3/4 mx-auto"
                border
                gradient
                :disabled="!!references.length"
                @click="refactorOrderComponent"
            >
                <span class="text-xl"> Dividir y asignar proveedores </span>
            </app-button>
        </div>
        <app-dialog
            v-model="isOpenModalResult"
            size="lg"
            @close="closeModalReult"
        >
            <template #header>
                <div class="w-full text-center py-2">
                    <div class="flex items-center justify-center gap-2">
                        <span class="text-4xl">✅</span>
                        <h2 class="text-theme-primary text-2xl font-bold">
                            Fragmentación Exitosa
                        </h2>
                    </div>
                    <p class="text-theme-muted text-sm mt-1">
                        Orden original #{{ id }}
                    </p>
                </div>
            </template>

            <div class="px-4 py-6">
                <div
                    v-if="orderGroupResult.orderGroupId"
                    class="mb-6 bg-theme-canvas rounded-lg p-4"
                >
                    <div class="flex items-center gap-2 mb-3">
                        <span class="text-3xl">🧔</span>
                        <h3 class="text-theme-primary text-xl font-semibold">
                            Orden Madre
                        </h3>
                    </div>
                    <div class="flex justify-center">
                        <a
                            class="bg-brand-blue font-bold text-white w-7 h-7 rounded-full flex items-center justify-center"
                            target="_blank"
                            rel="noopener"
                            :href="`/app/orders-groups/detail/${orderGroupResult.orderGroupId}`"
                        >
                            Ver Orden Madre #{{ orderGroupResult.orderGroupId }}
                        </a>
                    </div>
                </div>

                <div>
                    <div class="flex items-center gap-2 mb-4">
                        <span class="text-3xl">👶</span>
                        <h3 class="text-theme-primary text-xl font-semibold">
                            Órdenes Generadas
                            <span class="text-theme-muted text-base">
                                ({{ orderGroupResult.ordersResult.length }})
                            </span>
                        </h3>
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div
                            v-for="orderChild in orderGroupResult.ordersResult"
                            :key="orderChild.id"
                            class="bg-theme-canvas rounded-lg p-4 border-l-4 border-theme-success hover:bg-theme-hover transition-all"
                        >
                            <div
                                class="flex items-center justify-between mb-3 pb-2 border-b border-color"
                            >
                                <h4
                                    class="text-theme-primary text-lg font-bold"
                                >
                                    Orden #{{ orderChild.id }}
                                </h4>
                                <a
                                    :href="`/app/orders/detail/${orderChild.id}`"
                                    class="bg-theme-success font-bold text-white w-4 h-4 rounded-full flex items-center justify-center"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    Ver Detalle
                                </a>
                            </div>

                            <div class="space-y-2">
                                <p class="text-theme-muted text-sm mb-2">
                                    📦 Productos ({{
                                        orderChild.references.length
                                    }})
                                </p>
                                <div
                                    v-for="ref in orderChild.references"
                                    :key="ref.id"
                                    class="bg-brand-blue rounded p-2 text-sm"
                                >
                                    <div
                                        class="flex items-start justify-between gap-2"
                                    >
                                        <div class="flex-1">
                                            <p
                                                class="text-theme-base font-semibold"
                                            >
                                                {{ ref.reference.product.name }}
                                            </p>
                                            <div
                                                class="flex flex-wrap gap-2 mt-1 text-xs text-theme-muted"
                                            >
                                                <span>
                                                    SKU:
                                                    {{
                                                        ref.reference.product
                                                            .sku
                                                    }}
                                                </span>
                                                <span>•</span>
                                                <span>
                                                    Talla:
                                                    {{ ref.reference.size }}
                                                </span>
                                                <span>•</span>
                                                <span
                                                    class="flex items-center gap-1"
                                                >
                                                    Color:
                                                    <span
                                                        class="inline-block w-3 h-3 rounded-full border border-color"
                                                        :style="{
                                                            backgroundColor:
                                                                ref.reference
                                                                    .colorRef
                                                        }"
                                                    ></span>
                                                    {{ ref.reference.color }}
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            class="text-right flex-shrink-0 ml-2"
                                        >
                                            <p
                                                class="text-theme-primary font-bold text-base"
                                            >
                                                x{{ ref.quantity }}
                                            </p>
                                            <p class="text-theme-muted text-xs">
                                                {{ ref.priceOffer | money }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="mt-3 pt-3 border-t border-color text-sm"
                            >
                                <div
                                    class="flex justify-between text-theme-base"
                                >
                                    <span>Total productos:</span>
                                    <span class="font-semibold">
                                        {{
                                            orderChild.references.reduce(
                                                (sum, ref) =>
                                                    sum + ref.quantity,
                                                0
                                            )
                                        }}
                                        unidades
                                    </span>
                                </div>
                                <div
                                    class="flex justify-between text-theme-primary font-bold mt-1"
                                >
                                    <span>Total:</span>
                                    <span>
                                        {{
                                            orderChild.references.reduce(
                                                (sum, ref) =>
                                                    sum +
                                                    ref.priceOffer *
                                                        ref.quantity,
                                                0
                                            ) | money
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex justify-center mt-6">
                    <app-button primary size="large" @click="closeModalReult">
                        Ir a Lista de Órdenes
                    </app-button>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { Container, Draggable } from "vue-smooth-dnd";
import { mapActions, mapGetters } from "vuex";

import { DEFAULT_IMAGE_URL } from "@/common/constants";
import { applyDrag } from "@/common/lib/drag";

import ReferenceTableItem2 from "@/components/references/ReferenceTableItem2.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import ProcessGuide from "@/components/utils/ProcessGuide.vue";

export default {
    name: "OrdersRefactor",
    components: {
        AppDialog,
        Container,
        Draggable,
        ReferenceTableItem2,
        ProcessGuide
    },
    data: () => ({
        itemComponent: {
            id: 76,
            orderGroupId: null,
            references: [
                {
                    id: 77,
                    size: "23",
                    color: "gris",
                    colorRef: "#999999",
                    product: {
                        id: 34,
                        sku: "RBLL003",
                        name: "Botas para La Lluvia de Hipopotamo",
                        slug: "botas-para-la-lluvia-de-hipopotamo"
                    },
                    images: [
                        {
                            id: 148,
                            url: DEFAULT_IMAGE_URL,
                            alt: "Bota de niño gris con hipopotamo foto principal",
                            sequence: {
                                sequence: 0
                            }
                        }
                    ],
                    references: null,
                    price: 150000,
                    quantity: 17,
                    priceOffer: 140000,
                    appliedDiscountPerReference: 0,
                    merchants: []
                }
            ],
            location: {
                cityCode: "11001",
                address: "cra 42 F # 78 C - 63 SUR",
                coordinates: {
                    latitude: 0,
                    longitude: 0
                },
                neighborhood: "",
                city: "Bogotá D.C."
            },
            pickupMerchant: null,
            merchantsInventories: [
                {
                    merchantId: "",
                    merchantType: "",
                    name: "",
                    address: "",
                    location: {
                        latitude: 4.574501,
                        longitude: -74.1163301
                    },
                    cityCode: "11001",
                    references: [
                        {
                            referenceId: 79,
                            priceMerchant: 140000,
                            priceOfferMerchant: 140000,
                            priceAgreedMerchant: 140000,
                            stockMerchant: 1,
                            minStockMerchant: 0
                        }
                    ]
                }
            ],
            supplyMerchant: null
        },
        references: [],
        available: [],
        availableTmp: [],
        isLoading: false,
        hasLoadedBase: false,
        enableHelp: false,
        id: 0,
        $dropPlaceholderOptions: {
            className: "drop-preview",
            animationDuration: "150",
            showOnTop: true
        },
        orderGroupResult: {
            orderId: null,
            orderGroupId: null,
            ordersResult: []
        },
        isOpenModalResult: false
    }),
    computed: { ...mapGetters("control", ["backgroundColor"]) },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (
                !(await verifyPagePermission(
                    "order:Consultancy",
                    "/app/orders"
                ))
            )
                return;
            this.id = Number(this.$route.params.id);
            return this.consultancyOrderComponent();
        }
    },
    methods: {
        ...mapActions("orders", ["consultancyOrder", "refactorOrder"]),
        async consultancyOrderComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.consultancyOrder({
                    id: this.id
                });
                this.references = [...this.itemComponent.references];
                this.available = this.itemComponent.merchantsInventories.map(
                    (item) => ({
                        ...item,
                        children: []
                    })
                );
                this.hasLoadedBase = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/orders");
                }
            }
        },
        async refactorOrderComponent() {
            const orders = this.available.filter(
                (item) => item.children.length > 0
            );
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Deseas fragmentar la orden ${this.id}`,
                text:
                    orders.length > 1
                        ? `Estas seguro que deseas fragementar la orden en ${orders.length} ordenes, recuerda que no puedes podrás editar los productos de las ordenes resultantes ni aplicar descuentos.`
                        : "Como la fragementación solo tiene un tiendase reasignará la orden, estas seguro que deseas hacer la operación.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (!dialogResult.isConfirmed) {
                return;
            }

            this.isLoading = true;
            this.$loading.show();
            try {
                const result = await this.refactorOrder({
                    id: this.id,
                    orders: orders.map((item) => ({
                        merchantId: item.merchantId,
                        merchantType: item.merchantType,
                        references: item.children.map((item2) => ({
                            id: item2.id,
                            quantity: item2.quantity
                        }))
                    }))
                });
                this.$sound.play();
                this.$loading.hide();
                this.orderGroupResult = { ...result };
                this.isOpenModalResult = true;
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        getReferencesPayload() {
            return (index) => {
                return this.references[index]; // payload send in onDrop reference
            };
        },
        onDropReferenceOfReferences(dropResult) {
            console.log("drop", dropResult);
            if (
                dropResult.removedIndex !== null ||
                dropResult.addedIndex !== null
            ) {
                let references = [...this.references];
                references = applyDrag(references, dropResult).map((item) => {
                    delete item.priceAgreedMerchant;
                    delete item.priceAgreedMerchant;
                    delete item.priceOfferMerchant;
                    delete item.priceMerchant;
                    delete item.priceAgreedMerchant;
                    delete item.stockMerchant;
                    delete item.minStockMerchant;
                    return item;
                });
                this.references = references;
            }
        },
        getReferencesInMerchantPayload({ merchantId, merchantType }) {
            return (index) => {
                const merchant = this.available.find(
                    (p) =>
                        p.merchantId === merchantId &&
                        p.merchantType === merchantType
                );
                if (!merchant) {
                    console.error(
                        "Merchant not found:",
                        merchantId,
                        merchantType
                    );
                    return null;
                }
                return merchant.children[index]; // payload send in onDrop
            };
        },
        onDropReferenceOfMerchant({ merchantId, merchantType }, dropResult) {
            console.log("drop en merchant", {
                merchantId,
                merchantType,
                dropResult
            });
            if (
                dropResult.removedIndex !== null ||
                dropResult.addedIndex !== null
            ) {
                const available = [...this.available];
                const column = available.find(
                    (p) =>
                        p.merchantId === merchantId &&
                        p.merchantType === merchantType
                );
                const columnIndex = available.findIndex(
                    (p) =>
                        p.merchantId === merchantId &&
                        p.merchantType === merchantType
                );
                const newColumn = { ...column };
                const children = applyDrag(newColumn.children, dropResult);
                newColumn.children = children.map((item) => {
                    const foundInventory = item.merchants.find(
                        (item2) =>
                            item2.merchantId === merchantId &&
                            item2.merchantType === merchantType
                    );
                    return {
                        ...item,
                        ...{
                            ...(foundInventory ? foundInventory : null)
                        }
                    };
                });
                available.splice(columnIndex, 1, newColumn);
                this.available = available;
            }
        },
        onEndDrag() {
            console.log("*** onEndDrag ***");
            const available = [...this.available, ...this.availableTmp].map(
                (item) => ({ ...item, stockMerchant: 0 })
            );
            this.availableTmp = [];
            this.available = [...available];
        },
        onStartDrag({ payload }) {
            console.log("*** onStartDrag ***");
            this.availableTmp = [];
            const available = [];
            this.available.forEach((item) => {
                const foundMerchant = payload.merchants.find(
                    (item2) =>
                        item2.merchantId === item.merchantId &&
                        item2.merchantType === item.merchantType
                );
                if (foundMerchant) {
                    available.push({ ...item, ...foundMerchant });
                } else {
                    this.availableTmp.push(item);
                }
            });
            this.available = [...available];
        },
        closeModalReult() {
            this.$router.push("/app/orders");
        }
    }
};
</script>
